import React, { useMemo } from 'react';
import { Release } from '@/types';
import ReleaseCard from './ReleaseCard';

interface ReleaseListProps {
  releases: Release[];
  updateRelease: (release: Release) => void;
  deleteRelease: (id: string) => void;
  canEdit?: boolean;
  canDeleteFn?: (release: Release) => boolean;
  clientId?: string;
  isCompletedReleases?: boolean;
  statusGroup: string;
  expandedReleaseId: string | null;
}

const ReleaseList: React.FC<ReleaseListProps> = ({ 
  releases, 
  updateRelease, 
  deleteRelease, 
  canEdit = true,
  canDeleteFn,
  clientId,
  isCompletedReleases = false,
  statusGroup,
  expandedReleaseId
}) => {
  // Get the highest version number in this status group
  const highestVersion = useMemo(() => {
    const statusReleases = releases.filter(r => r.status === statusGroup);
    if (statusReleases.length === 0) return 0;

    return Math.max(...statusReleases.map(release => {
      const match = release.version.match(/^v?(\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    }));
  }, [releases, statusGroup]);

  // Filter and sort releases
  const sortedReleases = useMemo(() => {
    const filtered = releases.filter(r => r.status === statusGroup);
    return [...filtered].sort((a, b) => {
      const versionAMatch = a.version.match(/^v?(\d+)/);
      const versionBMatch = b.version.match(/^v?(\d+)/);
      
      const versionA = versionAMatch ? parseInt(versionAMatch[1], 10) : 0;
      const versionB = versionBMatch ? parseInt(versionBMatch[1], 10) : 0;
      
      if (isCompletedReleases) {
        return versionB - versionA; // Descending for completed
      } else {
        return versionA - versionB; // Ascending for others
      }
    });
  }, [releases, statusGroup, isCompletedReleases]);

  return (
    <div className="space-y-6">
      {sortedReleases.length === 0 ? (
        <div className="text-center py-10 border rounded-md bg-gray-50">
          <p className="text-gray-500">No releases found.</p>
        </div>
      ) : (
        sortedReleases.map((release) => {
          const versionMatch = release.version.match(/^v?(\d+)/);
          const version = versionMatch ? parseInt(versionMatch[1], 10) : 0;
          
          return (
            <ReleaseCard
              key={release.id}
              release={release}
              updateRelease={updateRelease}
              deleteRelease={deleteRelease}
              canEdit={canEdit}
              canDelete={canDeleteFn ? canDeleteFn(release) : canEdit}
              clientId={clientId}
              defaultOpen={release.id === expandedReleaseId}
            />
          );
        })
      )}
    </div>
  );
};

export default ReleaseList;
